import { FC, useEffect } from 'react';
import { useNotificationCountPoll } from 'app/components/Notifications';
import { fetchUnseenNotificationCountEffect } from 'app/store/Notifications/effects';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getTotalNotificationsCount,
  getUnseenNotificationCountPollingInterval,
} from 'app/store/Notifications/selectors';
import {
  getApiKeyWebSelector,
  getAuthenticatedSelector,
  getBootstrapLoadingState,
} from 'app/store/Auth/selectors';
import { isLoading, shouldFetch } from 'app/utils/utils';
import {
  getCurrentProfile,
  getProfileLoadingStateSelector,
  getSmallProfileImage,
} from 'app/store/Profile/selectors';
import { fetchLegalProfileEffect } from 'app/store/Profile/effects';
import { getPlatform } from 'app/store/Electron/selectors';
import { routes } from 'app/routing/routesConfig';
import { useNavigate } from 'react-router-dom';
import { getIsWebSocketLive } from 'app/store/WebSockets/selectors';
import { HeaderBarComponent } from './HeaderBarComponent';

interface HeaderBarProps {
  isWeb?: boolean;
}

export const HeaderBar: FC<HeaderBarProps> = ({ isWeb = false }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(getAuthenticatedSelector);
  const isBoostrapLoading = isLoading(useAppSelector(getBootstrapLoadingState));
  const apiKey = useAppSelector(getApiKeyWebSelector);
  const isInitializing = isBoostrapLoading || !apiKey;

  const totalNotificationsCount = useAppSelector(getTotalNotificationsCount);
  const pollingInterval =
    useAppSelector(getUnseenNotificationCountPollingInterval) || 0;
  const profileLoadingState = useAppSelector(getProfileLoadingStateSelector);
  const currentProfile = useAppSelector(getCurrentProfile);
  const profileImage = useAppSelector(getSmallProfileImage);
  const platform = useAppSelector(getPlatform);
  const isWebSocketAlive = useAppSelector(getIsWebSocketLive);

  useNotificationCountPoll(
    fetchUnseenNotificationCountEffect(),
    isWebSocketAlive ? 0 : pollingInterval
  );

  const redirectToSignOut = () =>
    navigate(routes.signOut.path, { replace: true });

  useEffect(() => {
    if (shouldFetch(profileLoadingState) && isAuthenticated) {
      dispatch(fetchLegalProfileEffect());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <HeaderBarComponent
      isWeb={isWeb}
      totalNotificationsCount={totalNotificationsCount}
      isAuthenticated={isAuthenticated}
      avatar={profileImage}
      signOut={redirectToSignOut}
      isInitializing={isInitializing}
      platform={platform}
      initials={currentProfile?.initials}
      colors={currentProfile?.avatarColors}
    />
  );
};
